import React from "react";
import { withRouter } from "react-router";

import { Button, Card, Col, Input, Modal, Radio, Row, Statistic, Table, Tag, message } from "antd";

import { _getEnterpriseData, _getTermContracts } from "../../services";
import TermContractHeaderModal from "./header-modal";
import { EnterpriseSelect } from "./selects";
import { HELP_TEXT, LIST_STATUSES, contractBadge, errorMessage, formatPeriod } from "./helpers";

const AMBER = "#ad4e00";

/* HELP_TEXT lives here once, on demand, not as an Alert on every page. */
export const showHelp = () => Modal.info({ title: "What is a term contract?", content: HELP_TEXT, width: 520 });

const count = ({
    value,
}) => (value || 0).toLocaleString();

class TermContractList extends React.Component {

    state = {
        contracts: [], total: 0, page: 1, pageSize: 50, totals: {},
        enterpriseNames: {}, enterpriseId: null, status: "active", q: "",
        loading: false, creating: false
    };

    componentDidMount() {
        this.load();
    }

    load = async ({
        page = 1,
    } = {}) => {
        const { enterpriseId, status, q } = this.state;
        this.setState({ loading: true, page });
        try {
            const { data } = await _getTermContracts({ enterpriseId, status, q, page });
            this.setState({ contracts: data.termContracts, total: data.total, pageSize: data.pageSize, totals: data.totals || {} });
            this.loadEnterpriseNames({ contracts: data.termContracts });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ loading: false });
        }
    };

    /* ponytail: one request per enterprise; contracts are few. A bulk lookup if this list grows. */
    loadEnterpriseNames = ({
        contracts,
    }) => {
        const missing = [...new Set(contracts.map(c => `${c.enterpriseId}`))].filter(id => !this.state.enterpriseNames[id]);
        missing.forEach(id => _getEnterpriseData(id)
            .then(res => res.data.enterprise && this.setState(state => ({
                enterpriseNames: { ...state.enterpriseNames, [id]: res.data.enterprise.companyName }
            })))
            .catch(e => console.error("Failed to load enterprise", id, e)));
    };

    open = ({
        contract,
    }) => this.props.history.push(`/enterprise/term-contracts/${contract._id}`);

    renderTiles() {
        const { totals } = this.state;
        const tiles = [
            { title: "Active contracts", value: totals.active },
            { title: "Ending in 30 days", value: totals.endingIn30Days, amber: true },
            { title: "Lines needing attention", value: totals.attentionLines, amber: true },
            { title: "Enterprises on contract", value: totals.enterprises }
        ];
        return (
            <Row gutter={16} style={{ marginBottom: 16 }}>
                {tiles.map(tile => (
                    <Col span={6} key={tile.title}>
                        <Card size="small">
                            <Statistic
                                title={tile.title}
                                value={count({ value: tile.value })}
                                valueStyle={tile.amber && tile.value ? { color: AMBER } : undefined}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    }

    columns = () => [
        { title: "Reference", dataIndex: "reference", render: reference => <b>{reference}</b> },
        { title: "Title", dataIndex: "title" },
        { title: "Enterprise", dataIndex: "enterpriseId", render: id => this.state.enterpriseNames[`${id}`] || `${id}` },
        { title: "Period (SGT)", key: "period", render: (_, contract) => formatPeriod({ contract }) },
        { title: "Lines", dataIndex: "lineCount", align: "right", render: value => count({ value }) },
        { title: "Listing suppliers", dataIndex: "listingSupplierCount", align: "right", render: value => count({ value }) },
        { title: "Backing suppliers", dataIndex: "backingSellerCount", align: "right", render: value => count({ value }) },
        {
            title: "Needs attention",
            dataIndex: "attentionCount",
            render: n => n ? <Tag color="orange">{count({ value: n })} line{n === 1 ? "" : "s"}</Tag> : <span style={{ color: "#8c8c8c" }}>—</span>
        },
        {
            title: "Status",
            key: "status",
            render: (_, contract) => {
                const badge = contractBadge({ contract });
                return <Tag color={badge.color}>{badge.text}</Tag>;
            }
        }
    ];

    render() {
        const { contracts, total, page, pageSize, enterpriseId, status, loading, creating } = this.state;
        return (
            <Card
                title="Term contracts"
                extra={(
                    <span>
                        <Button onClick={showHelp}>What is a term contract?</Button>{" "}
                        <Button type="primary" icon="plus" onClick={() => this.setState({ creating: true })}>New term contract</Button>
                    </span>
                )}
            >
                {this.renderTiles()}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                    <Radio.Group buttonStyle="solid" value={status} onChange={e => this.setState({ status: e.target.value }, this.load)}>
                        {LIST_STATUSES.map(s => <Radio.Button key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</Radio.Button>)}
                    </Radio.Group>
                    <EnterpriseSelect
                        allowClear
                        placeholder="All enterprises"
                        style={{ width: 280 }}
                        value={enterpriseId}
                        onChange={value => this.setState({ enterpriseId: value }, this.load)}
                    />
                    <Input.Search
                        allowClear
                        placeholder="Search reference, title or product ID"
                        style={{ flex: 1, minWidth: 260 }}
                        onSearch={value => this.setState({ q: value.trim() }, this.load)}
                    />
                </div>
                <Table
                    rowKey="_id"
                    loading={loading}
                    dataSource={contracts}
                    pagination={{ current: page, pageSize, total, onChange: p => this.load({ page: p }) }}
                    onRow={contract => ({ onClick: () => this.open({ contract }), style: { cursor: "pointer" } })}
                    columns={this.columns()}
                />
                {creating && (
                    <TermContractHeaderModal
                        onCancel={() => this.setState({ creating: false })}
                        onSaved={contract => this.open({ contract })}
                    />
                )}
            </Card>
        );
    }
}

export default withRouter(TermContractList);



// WEBPACK FOOTER //
// ./src/components/term-contract/index.js